import React from 'react'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'
import CDInput from 'components/cd-input'
import TextArea from 'components/text-area'
import CDImage from 'components/cd-image-upload'
import CDButton from 'components/cd-button'
import NativeSelect from '../components/native-select'
import GalleryUpload from '../components/gallery-upload'
import { productsApi } from '../restaurant.api'
import { useProducts } from './products.context'

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'draft', label: 'Draft' },
  { value: 'archived', label: 'Archived' },
]

const validationSchema = Yup.object({
  name: Yup.string().required('Product name is required'),
  category: Yup.string().required('Category is required'),
  price: Yup.number()
    .typeError('Price must be a number')
    .min(0, 'Price cannot be negative')
    .required('Price is required'),
  salePrice: Yup.number()
    .typeError('Sale price must be a number')
    .min(0, 'Sale price cannot be negative')
    .nullable(),
})

const toInitialValues = (product) => {
  const category =
    typeof product?.category === 'object'
      ? product.category?._id
      : product?.category
  return {
    name: product?.name || '',
    category: category || '',
    description: product?.description || '',
    shortDescription: product?.shortDescription || '',
    price: product?.price ?? '',
    salePrice: product?.salePrice ?? '',
    calories: product?.calories ?? '',
    preparationTime: product?.preparationTime ?? '',
    tags: (product?.tags || []).join(', '),
    status: product?.status || 'active',
    isFeatured: product?.isFeatured || false,
    isRecommended: product?.isRecommended || false,
    isAvailable: product?.isAvailable ?? true,
    isVisible: product?.isVisible ?? true,
    image: product?.image || '',
    gallery: product?.gallery || [],
  }
}

function CheckboxField({ formik, name, label }) {
  return (
    <label className='tw-flex tw-items-center tw-gap-2 tw-text-sm tw-cursor-pointer'>
      <input
        type='checkbox'
        checked={!!formik.values[name]}
        onChange={(e) => formik.setFieldValue(name, e.target.checked)}
        className='tw-w-4 tw-h-4 tw-cursor-pointer'
      />
      {label}
    </label>
  )
}

function ProductForm({ product, onClose }) {
  const { categories, refresh } = useProducts()
  const isEditing = !!product?._id

  const handleSubmit = async (values, { setSubmitting }) => {
    const payload = {
      ...values,
      price: Number(values.price),
      salePrice: values.salePrice === '' ? undefined : Number(values.salePrice),
      calories: values.calories === '' ? undefined : Number(values.calories),
      preparationTime:
        values.preparationTime === ''
          ? undefined
          : Number(values.preparationTime),
      tags: values.tags
        ? values.tags.split(',').map((tag) => tag.trim()).filter(Boolean)
        : [],
    }
    delete payload.imageName // set by cd-image-upload, not part of the model

    const { response } = isEditing
      ? await productsApi.update(product._id, payload)
      : await productsApi.create(payload)

    setSubmitting(false)
    if (response?.success) {
      await refresh()
      onClose()
    }
  }

  return (
    <Formik
      initialValues={toInitialValues(product)}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <Form className='tw-flex tw-flex-col tw-gap-4'>
          <CDInput name='name' label='Product name' required />
          <NativeSelect
            name='category'
            label='Category'
            required
            placeholder='Select a category'
            options={categories.map((category) => ({
              value: category._id,
              label: category.name,
            }))}
          />
          <TextArea name='description' label='Description' />
          <CDInput name='shortDescription' label='Short description' />
          <div className='tw-flex tw-gap-4'>
            <CDInput name='price' label='Price' type='number' required />
            <CDInput name='salePrice' label='Sale price' type='number' />
          </div>
          <div className='tw-flex tw-gap-4'>
            <CDInput name='calories' label='Calories' type='number' />
            <CDInput
              name='preparationTime'
              label='Prep time (min)'
              type='number'
            />
          </div>
          <CDInput name='tags' label='Tags (comma separated)' />
          <NativeSelect name='status' label='Status' options={STATUS_OPTIONS} />
          <div className='tw-flex tw-flex-wrap tw-gap-4 tw-py-1'>
            <CheckboxField formik={formik} name='isFeatured' label='Featured' />
            <CheckboxField
              formik={formik}
              name='isRecommended'
              label='Recommended'
            />
            <CheckboxField
              formik={formik}
              name='isAvailable'
              label='Available'
            />
            <CheckboxField formik={formik} name='isVisible' label='Visible' />
          </div>
          <CDImage name='image' label='Product image' />
          <GalleryUpload name='gallery' label='Gallery images' />

          <div className='tw-flex tw-gap-3 tw-mt-2'>
            <CDButton
              type='submit'
              processing={formik.isSubmitting}
              label={isEditing ? 'Save changes' : 'Create product'}
            />
            <button
              type='button'
              onClick={onClose}
              className='tw-px-[24px] tw-py-[8px] tw-rounded-[6px] tw-border tw-border-gray-300 tw-text-gray-600 tw-text-regular'
            >
              Cancel
            </button>
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default ProductForm
